import * as R from 'ramda';
import {
  CrossChannelMetrics,
  Metrics,
  MetricsByChannel,
  MetricTotalsByDate
} from '@blisspointmedia/bpm-types/dist/CrossChannel';
import {
  CompanyFiscalCal,
  DateGroupingKey,
  getGroupingValue
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { ratioAggregator } from '@src/utils/metrics/ratioAggregation';

export interface MetricTotalsByDateChannel {
  date: string;
  channels: MetricsByChannel;
}

/** Recomputes each ratio metric in `metrics` from the summed components with its registry
 *  recipe. A ratio with no value (empty or missing denominator) is null, which charts draw as a gap. */
export const withRecomputedRatios = (totals: Metrics, metrics: readonly string[]): Metrics => {
  const result: Record<string, number | null> = { ...totals };
  for (const metric of metrics) {
    const aggregator = ratioAggregator(metric);
    if (aggregator) {
      result[metric] = aggregator(totals);
    }
  }
  // Ratios may now be null (charts draw a gap); `Metrics` predates that and types them as numbers.
  return result as Metrics;
};

/** The ratio over the summed components of `rows`; undefined when `metric` has no ratio recipe. */
export const blendedRatio = (
  metric: string,
  rows: (Metrics | undefined)[]
): number | null | undefined => {
  const aggregator = ratioAggregator(metric);
  if (!aggregator) {
    return undefined;
  }
  const totals = rows.reduce<Partial<Metrics>>(
    (acc, row) => (row ? R.mergeWith(R.add, acc, row) : acc),
    {}
  );
  return aggregator(totals);
};

/**
 * Depending on the date grouping selected (Day, Week, Month, etc.), sum up the metrics for each date.
 * Day data is already at the daily level, so it's returned as-is.
 */
export const totalsByDateIncrement = (
  data: MetricTotalsByDate[],
  dateGrouping: DateGroupingKey,
  metricOptions: {
    value: string;
    isCustom?: boolean;
    fetchGetter?: (metrics: Metrics) => number;
  }[],
  fiscalCal: CompanyFiscalCal
): MetricTotalsByDate[] => {
  if (dateGrouping === 'Day') {
    return data;
  }

  const totalsByDate: Record<string, Metrics> = {};
  for (const { date, metrics } of data) {
    const dateToUse = getGroupingValue(date, dateGrouping, fiscalCal);
    totalsByDate[dateToUse] = R.mergeWith(R.add, totalsByDate[dateToUse] || {}, metrics);
  }

  const offeredMetrics = metricOptions.map(option => option.value);
  return Object.entries(totalsByDate)
    .map(([date, totals]) => {
      // Every ratio in the data is recomputed, not just the offered ones: the export includes them.
      let metrics = withRecomputedRatios(totals, [
        ...new Set([...offeredMetrics, ...Object.keys(totals)])
      ]);
      for (const option of metricOptions) {
        if (option.isCustom && option.fetchGetter) {
          metrics = { ...metrics, [option.value]: option.fetchGetter(totals) };
        }
      }
      return { date, metrics };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Depending on the date grouping selected (Day, Week, Month, etc.), sum up each channel's metrics
 * for each date, then recompute the ratio metrics in `metrics`.
 */
export const totalsByDateIncrementChannel = (
  data: CrossChannelMetrics[],
  dateGrouping: DateGroupingKey,
  fiscalCal: CompanyFiscalCal,
  metrics: readonly string[]
): MetricTotalsByDateChannel[] => {
  const totalsByDateChannel: Record<string, MetricsByChannel> = {};
  for (const { date, channel, metrics: channelMetrics } of data) {
    const dateToUse = getGroupingValue(date, dateGrouping, fiscalCal);
    totalsByDateChannel[dateToUse] = totalsByDateChannel[dateToUse] || {};
    totalsByDateChannel[dateToUse][channel] = R.mergeWith(
      R.add,
      totalsByDateChannel[dateToUse][channel] || {},
      channelMetrics
    );
  }

  return Object.entries(totalsByDateChannel)
    .map(([date, channels]) => ({
      date,
      channels: Object.fromEntries(
        Object.entries(channels).map(([channel, totals]) => [
          channel,
          withRecomputedRatios(totals, metrics)
        ])
      )
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

type ChannelPrefix = 'channels' | 'channels_other';

interface ChannelTooltipEntry {
  dataKey?: string;
  value?: number | null;
  payload?: Partial<Record<ChannelPrefix, MetricsByChannel>>;
}

/** Tooltip footer for `prefix`'s lines: a ratio metric's recipe over the summed components of every
 *  channel on the chart (recharts drops null-valued lines from `entries`, so their components would
 *  go missing), otherwise the sum of the plotted values. */
export const channelTooltipTotal = (
  metric: string,
  prefix: ChannelPrefix,
  channels: readonly string[],
  entries: ChannelTooltipEntry[] = []
): number | null => {
  const row = entries[0]?.payload?.[prefix];
  const blended = blendedRatio(
    metric,
    channels.map(channel => row?.[channel])
  );
  if (blended !== undefined) {
    return blended;
  }
  return entries
    .filter(({ dataKey }) => dataKey?.startsWith(`${prefix}.`))
    .reduce((sum, { value }) => sum + (value || 0), 0);
};
